import React, { useState, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import toast from "react-hot-toast";
import admissionService from "../../../Services/admissionService";
import doctorService from "../../../Services/doctorService";
import patientService from "../../../Services/patientService";
import hospitalStructureService from "../../../Services/Hospitalstructureservice";


const SectionLabel = ({ children }) => (
  <p className="text-[11px] uppercase tracking-widest font-[600] text-gray-400 mb-3 mt-6 sm:mt-8 border-b border-gray-100 pb-2">
    {children}
  </p>
);

const InputField = ({ label, hint, ...props }) => (
  <div className="flex flex-col gap-1">
    <label className="text-[12px] font-[500] text-gray-500">
      {label}{" "}
      {hint && <span className="text-gray-400 font-[400]">{hint}</span>}
    </label>
    <input
      {...props}
      className="w-full px-3 py-[9px] text-[13px] text-gray-800 bg-white border border-gray-200 rounded-lg outline-none hover:border-orange-200 focus:border-orange-300 focus:ring-2 focus:ring-orange-100 transition-colors"
    />
  </div>
);

const CustomSelect = ({ label, value, onChange, name, options, hint }) => {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const handler = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  return (
    <div className="flex flex-col gap-1" ref={ref}>
      <label className="text-[12px] font-[500] text-gray-500">
        {label}{" "}
        {hint && <span className="text-gray-400 font-[400]">{hint}</span>}
      </label>
      <div className="relative">
        <button
          type="button"
          onClick={() => setOpen((prev) => !prev)}
          className={`w-full px-3 py-[9px] text-[13px] text-left bg-white border rounded-lg outline-none transition-colors flex items-center justify-between
            hover:border-orange-200
            ${open ? "border-orange-300 ring-2 ring-orange-100" : "border-gray-200"}
            ${value ? "text-gray-800" : "text-gray-400"}`}
        >
          <span className="truncate pr-2">{value || `Select ${label}`}</span>
          <svg
            className={`w-4 h-4 text-gray-400 transition-transform duration-200 flex-shrink-0 ${open ? "rotate-180" : ""}`}
            fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}
          >
            <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
          </svg>
        </button>

        {open && (
          <div className="absolute z-20 mt-1 w-full max-w-[calc(100vw-2rem)] bg-white border border-gray-200 rounded-lg shadow-lg overflow-hidden max-h-[200px] overflow-y-auto">
            {options.map((opt) => {
              const val = typeof opt === "object" ? opt.value : opt;
              const label_ = typeof opt === "object" ? opt.label : opt;
              return (
                <button
                  key={val}
                  type="button"
                  onClick={() => {
                    onChange({ target: { name, value: val } });
                    setOpen(false);
                  }}
                  className={`w-full px-3 py-[9px] text-[13px] text-left transition-colors
                    hover:bg-gray-50 hover:text-orange-600
                    ${value === val ? "bg-orange-50 text-orange-600 font-[500]" : "text-gray-700"}`}
                >
                  {label_}
                </button>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

const ValidatedField = ({ label, value, maxLength, onChange, placeholder, hint, name }) => {
  const isComplete = value.length === maxLength;
  const isPartial = value.length > 0 && value.length < maxLength;
  return (
    <div className="flex flex-col gap-1">
      <div className="flex flex-wrap items-center justify-between gap-1">
        <label className="text-[12px] font-[500] text-gray-500">{label}</label>
        {isPartial && (
          <span className="text-[11px] text-red-400">{maxLength - value.length} digits remaining</span>
        )}
        {isComplete && (
          <span className="text-[11px] text-green-500 font-[500]">✓ Valid {hint}</span>
        )}
      </div>
      <input
        type="text"
        name={name}
        value={value}
        onChange={onChange}
        maxLength={maxLength}
        placeholder={placeholder}
        className={`w-full px-3 py-[9px] text-[13px] text-gray-800 bg-white border rounded-lg outline-none transition-colors
          hover:border-orange-200 focus:ring-2 focus:ring-orange-100
          ${isComplete
            ? "border-green-400 focus:border-green-400"
            : isPartial
            ? "border-red-300 focus:border-orange-200"
            : "border-gray-200 focus:border-orange-300"
          }`}
      />
    </div>
  );
};

const DOBInput = ({ value, onChange }) => {
  const dateInputRef = useRef(null);
  const [focused, setFocused] = useState(false);
  const [activeSegment, setActiveSegment] = useState(null);

  const parseISO = (iso) => {
    if (!iso) return { dd: "", mm: "", yyyy: "" };
    const [y, m, d] = iso.split("-");
    return { dd: d || "", mm: m || "", yyyy: y || "" };
  };

  const [segments, setSegments] = useState(() => parseISO(value));
  useEffect(() => { setSegments(parseISO(value)); }, [value]);

  const pushChange = (dd, mm, yyyy) => {
    if (dd && mm && yyyy && yyyy.length === 4) {
      onChange({ target: { name: "dOB", value: `${yyyy}-${mm.padStart(2, "0")}-${dd.padStart(2, "0")}` } });
    } else {
      onChange({ target: { name: "dOB", value: "" } });
    }
  };

  const handleCalendarPick = (e) => {
    const iso = e.target.value;
    setSegments(parseISO(iso));
    onChange({ target: { name: "dOB", value: iso } });
  };

  const handleKeyDown = (e, segment) => {
    const { dd, mm, yyyy } = segments;
    if (e.key === "ArrowUp" || e.key === "ArrowDown") {
      e.preventDefault();
      const delta = e.key === "ArrowUp" ? 1 : -1;
      if (segment === "dd") {
        let d = parseInt(dd || "1") + delta;
        if (d < 1) d = 31; if (d > 31) d = 1;
        const n = String(d).padStart(2, "0");
        setSegments((s) => ({ ...s, dd: n })); pushChange(n, mm, yyyy);
      } else if (segment === "mm") {
        let m = parseInt(mm || "1") + delta;
        if (m < 1) m = 12; if (m > 12) m = 1;
        const n = String(m).padStart(2, "0");
        setSegments((s) => ({ ...s, mm: n })); pushChange(dd, n, yyyy);
      } else if (segment === "yyyy") {
        let y = parseInt(yyyy || new Date().getFullYear()) + delta;
        if (y < 1900) y = 1900; if (y > 2100) y = 2100;
        const n = String(y);
        setSegments((s) => ({ ...s, yyyy: n })); pushChange(dd, mm, n);
      }
    }
    if ((e.key === "/" || e.key === " ") && e.key !== "Tab") {
      e.preventDefault();
      if (segment === "dd") document.getElementById("adob_mm")?.focus();
      if (segment === "mm") document.getElementById("adob_yyyy")?.focus();
    }
    if (e.key === "Backspace") {
      if (segment === "mm" && mm === "") { e.preventDefault(); document.getElementById("adob_dd")?.focus(); }
      if (segment === "yyyy" && yyyy === "") { e.preventDefault(); document.getElementById("adob_mm")?.focus(); }
    }
  };

  const seg = (isActive) =>
    `bg-transparent outline-none text-[13px] text-gray-800 rounded px-0.5 transition-colors ${isActive ? "bg-orange-100 text-orange-700" : ""}`;

  return (
    <div className="flex flex-col gap-1">
      <label className="text-[12px] font-[500] text-gray-500">Date of Birth</label>
      <div
        className={`w-full px-3 py-[9px] bg-white border rounded-lg flex items-center justify-between transition-colors cursor-text hover:border-orange-200
          ${focused ? "border-orange-300 ring-2 ring-orange-100" : "border-gray-200"}`}
      >
        <div className="flex items-center gap-0.5 flex-1">
          <input id="adob_dd" type="text" inputMode="numeric" placeholder="DD" value={segments.dd}
            onChange={(e) => {
              const r = e.target.value.replace(/\D/g, "").slice(0, 2);
              setSegments((s) => ({ ...s, dd: r }));
              pushChange(r, segments.mm, segments.yyyy);
              if (r.length === 2) document.getElementById("adob_mm")?.focus();
            }}
            onKeyDown={(e) => handleKeyDown(e, "dd")}
            onFocus={() => { setFocused(true); setActiveSegment(0); }}
            onBlur={() => { setFocused(false); setActiveSegment(null); }}
            maxLength={2} className={seg(activeSegment === 0)} style={{ width: "26px" }} />
          <span className="text-gray-400 select-none">/</span>
          <input id="adob_mm" type="text" inputMode="numeric" placeholder="MM" value={segments.mm}
            onChange={(e) => {
              const r = e.target.value.replace(/\D/g, "").slice(0, 2);
              setSegments((s) => ({ ...s, mm: r }));
              pushChange(segments.dd, r, segments.yyyy);
              if (r.length === 2) document.getElementById("adob_yyyy")?.focus();
            }}
            onKeyDown={(e) => handleKeyDown(e, "mm")}
            onFocus={() => { setFocused(true); setActiveSegment(1); }}
            onBlur={() => { setFocused(false); setActiveSegment(null); }}
            maxLength={2} className={seg(activeSegment === 1)} style={{ width: "26px" }} />
          <span className="text-gray-400 select-none">/</span>
          <input id="adob_yyyy" type="text" inputMode="numeric" placeholder="YYYY" value={segments.yyyy}
            onChange={(e) => {
              const r = e.target.value.replace(/\D/g, "").slice(0, 4);
              setSegments((s) => ({ ...s, yyyy: r }));
              pushChange(segments.dd, segments.mm, r);
            }}
            onKeyDown={(e) => handleKeyDown(e, "yyyy")}
            onFocus={() => { setFocused(true); setActiveSegment(2); }}
            onBlur={() => { setFocused(false); setActiveSegment(null); }}
            maxLength={4} className={seg(activeSegment === 2)} style={{ width: "38px" }} />
        </div>
        <button
          type="button"
          onClick={() => dateInputRef.current?.showPicker()}
          className="text-gray-400 hover:text-orange-400 transition-colors ml-1 flex-shrink-0"
        >
          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
          </svg>
        </button>
        <input
          ref={dateInputRef}
          type="date"
          value={value || ""}
          onChange={handleCalendarPick}
          tabIndex={-1}
          style={{ position: "absolute", width: 0, height: 0, opacity: 0, pointerEvents: "none" }}
        />
      </div>
    </div>
  );
};

const HospitalStructureSelect = ({ value, onChange, structureList, loading, error }) => {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const handler = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const rooms = structureList.filter((e) => e.entry_type === "room");
  const wards = structureList.filter((e) => e.entry_type === "ward");

  const buildLabel = (entry) => {
    if (!entry) return "";
    if (entry.entry_type === "room") {
      return `Room — ${[entry.room_type, entry.room_number].filter(Boolean).join(" · ")}`;
    }
    return `Ward — ${[entry.ward_name, entry.bed_number ? "Bed " + entry.bed_number : null].filter(Boolean).join(" · ")}`;
  };

  const selected = structureList.find((e) => e.id === value);
  const displayText = selected ? buildLabel(selected) : null;
  const labelText = loading ? "Loading rooms & wards..." : error ? "Failed to load" : "Room / Ward";

  return (
    <div className="flex flex-col gap-1" ref={ref}>
      <label className="text-[12px] font-[500] text-gray-500">{labelText}</label>
      <div className="relative">
        <button
          type="button"
          disabled={loading || !!error}
          onClick={() => setOpen((p) => !p)}
          className={`w-full px-3 py-[9px] text-[13px] text-left bg-white border rounded-lg outline-none transition-colors flex items-center justify-between
            hover:border-orange-200 disabled:opacity-50 disabled:cursor-not-allowed
            ${open ? "border-orange-300 ring-2 ring-orange-100" : "border-gray-200"}
            ${displayText ? "text-gray-800" : "text-gray-400"}`}
        >
          <span className="flex items-center gap-2 min-w-0">
            {selected && (
              <span className={`text-[10px] font-[600] px-1.5 py-0.5 rounded-full flex-shrink-0
                ${selected.entry_type === "room" ? "bg-blue-50 text-blue-500" : "bg-green-50 text-green-600"}`}>
                {selected.entry_type === "room" ? "Room" : "Ward"}
              </span>
            )}
            <span className="truncate">{displayText || "Select room or ward"}</span>
          </span>
          <svg
            className={`w-4 h-4 text-gray-400 transition-transform duration-200 flex-shrink-0 ml-2 ${open ? "rotate-180" : ""}`}
            fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}
          >
            <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
          </svg>
        </button>

        {open && (
          <div className="absolute z-20 mt-1 w-full max-w-[calc(100vw-2rem)] bg-white border border-gray-200 rounded-lg shadow-lg overflow-hidden max-h-[240px] overflow-y-auto">
            <button
              type="button"
              onClick={() => { onChange(null); setOpen(false); }}
              className="w-full px-3 py-[9px] text-[12px] text-left text-gray-400 hover:bg-gray-50 border-b border-gray-100"
            >
              — None —
            </button>

            {rooms.length > 0 && (
              <>
                <div className="px-3 py-1.5 text-[10px] uppercase tracking-widest font-[600] text-gray-400 bg-gray-50 border-b border-gray-100">
                  Rooms
                </div>
                {rooms.map((entry) => (
                  <button
                    key={entry.id}
                    type="button"
                    onClick={() => { onChange(entry); setOpen(false); }}
                    className={`w-full px-3 py-[10px] text-[13px] text-left transition-colors hover:bg-orange-50 hover:text-orange-700
                      ${value === entry.id ? "bg-orange-50 text-orange-700 font-[500]" : "text-gray-700"}`}
                  >
                    <div className="flex items-start justify-between gap-2">
                      <span className="flex-1 min-w-0 truncate">{buildLabel(entry)}</span>
                      <span className="text-[11px] text-gray-400 font-[500] flex-shrink-0">
                        Rs. {Number(entry.rate_per_day).toLocaleString()}/day
                        {entry.discount_percent > 0 && (
                          <span className="ml-1.5 text-green-500">{entry.discount_percent}% off</span>
                        )}
                      </span>
                    </div>
                  </button>
                ))}
              </>
            )}

            {wards.length > 0 && (
              <>
                <div className="px-3 py-1.5 text-[10px] uppercase tracking-widest font-[600] text-gray-400 bg-gray-50 border-b border-gray-100 border-t">
                  Wards
                </div>
                {wards.map((entry) => (
                  <button
                    key={entry.id}
                    type="button"
                    onClick={() => { onChange(entry); setOpen(false); }}
                    className={`w-full px-3 py-[10px] text-[13px] text-left transition-colors hover:bg-orange-50 hover:text-orange-700
                      ${value === entry.id ? "bg-orange-50 text-orange-700 font-[500]" : "text-gray-700"}`}
                  >
                    <div className="flex items-start justify-between gap-2">
                      <span className="flex-1 min-w-0 truncate">{buildLabel(entry)}</span>
                      <span className="text-[11px] text-gray-400 font-[500] flex-shrink-0">
                        Rs. {Number(entry.rate_per_day).toLocaleString()}/day
                        {entry.discount_percent > 0 && (
                          <span className="ml-1.5 text-green-500">{entry.discount_percent}% off</span>
                        )}
                      </span>
                    </div>
                  </button>
                ))}
              </>
            )}

            {rooms.length === 0 && wards.length === 0 && (
              <div className="px-3 py-4 text-[13px] text-gray-400 text-center">No entries found.</div>
            )}
          </div>
        )}
      </div>

      {selected && (
        <div className="mt-1.5 flex flex-wrap items-center gap-2 px-3 py-2 bg-blue-50 border border-blue-100 rounded-lg">
          <span className={`text-[10px] font-[600] px-1.5 py-0.5 rounded-full flex-shrink-0
            ${selected.entry_type === "room" ? "bg-blue-100 text-blue-600" : "bg-green-100 text-green-700"}`}>
            {selected.entry_type === "room" ? "Room" : "Ward"}
          </span>
          <span className="text-[12px] text-gray-600 flex-1 min-w-0">{buildLabel(selected)}</span>
          <span className="text-[12px] font-[600] text-[#215187] flex-shrink-0">
            Rs. {Number(selected.rate_per_day).toLocaleString()}
            <span className="font-[400] text-gray-400">/day</span>
          </span>
        </div>
      )}
    </div>
  );
};


const EMPTY_FORM = {
  full_name: "", father_husband_name: "", CNIC: "", dOB: "",
  gender: "", marital_status: "", nationality: "", occupation: "", blood_group: "",
  contact_number: "", email: "", address: "", city: "",
  emergency_contact: "", guardian_relationship: "",
  allergies: "", medical_history: "", current_medications: "",
  previous_surgeries: "", admission_reason: "",
  doctor_assigned: "", department_ward: "",
  weight_kg: "", temperature_c: "", blood_pressure: "", pulse_rate: "", blood_sugar: "",
  admission_type: "", checkup_date: new Date().toISOString().split("T")[0],
  admission_datetime: "", room_number: "", bed_number: "",
  hospital_structure_id: null,
  referred_by: "",
  guardian_name: "", guardian_CNIC: "",
};


const AddAdmitPatient = () => {
  const navigate = useNavigate();

 
  const [formData, setFormData] = useState(EMPTY_FORM);
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);


  const [patientData, setPatientData] = useState([]);
  const [patientsLoading, setPatientsLoading] = useState(true);
  const [patientOpen, setPatientOpen] = useState(false);
  const [isExistingPatient, setIsExistingPatient] = useState(false);
  const patientSearchRef = useRef(null);

  
  const [doctors, setDoctors] = useState([]);
  const [doctorsLoading, setDoctorsLoading] = useState(true);


  const [structureList, setStructureList] = useState([]);
  const [structureLoading, setStructureLoading] = useState(true);
  const [structureError, setStructureError] = useState(null);



  useEffect(() => {
    const fetchPatients = async () => {
      try {
        const res = await patientService.fetchAllPatients();
        if (res.patients) setPatientData(res.patients);
      } catch (err) {
        toast.error("Failed to load patients.");
      } finally {
        setPatientsLoading(false);
      }
    };
    fetchPatients();
  }, []);

  useEffect(() => {
    const fetchDoctors = async () => {
      try {
        const res = await doctorService.fetchAllDoctors();
        if (res.doctors) setDoctors(res.doctors);
      } catch (err) {
        toast.error("Failed to load doctors.");
      } finally {
        setDoctorsLoading(false);
      }
    };
    fetchDoctors();
  }, []);

  useEffect(() => {
    const fetchStructure = async () => {
      try {
        const data = await hospitalStructureService.getAll();
        const list = Array.isArray(data)
          ? data
          : (data.structures ?? data.data ?? data.hospitalStructures ?? []);
        setStructureList(list);
      } catch (err) {
        setStructureError("Failed to load rooms & wards.");
      } finally {
        setStructureLoading(false);
      }
    };
    fetchStructure();
  }, []);


  useEffect(() => {
    const handler = (e) => {
      if (patientSearchRef.current && !patientSearchRef.current.contains(e.target)) {
        setPatientOpen(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);



  const filteredPatients = patientData.filter(
    (p) =>
      p.full_name?.toLowerCase().includes(formData.full_name.toLowerCase()) ||
      p.contact_number?.includes(formData.full_name) ||
      p.patient_id?.toString().includes(formData.full_name)
  );



  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (errors[name]) setErrors((prev) => ({ ...prev, [name]: "" }));
  };

  const handlePatientNameChange = (e) => {
    handleChange(e);
    setPatientOpen(true);

    if (isExistingPatient) setIsExistingPatient(false);
  };

  const handlePatientSelect = (patient) => {

    setFormData((prev) => ({
      ...prev,
      full_name:           patient.full_name           || prev.full_name,
      father_husband_name: patient.father_husband_name || prev.father_husband_name,
      CNIC:                patient.CNIC                || prev.CNIC,
      dOB:                 patient.dOB                 || prev.dOB,
      gender:              patient.gender              || prev.gender,
      marital_status:      patient.marital_status      || prev.marital_status,
      nationality:         patient.nationality         || prev.nationality,
      occupation:          patient.occupation          || prev.occupation,
      blood_group:         patient.blood_group         || prev.blood_group,
      contact_number:      patient.contact_number      || prev.contact_number,
      email:               patient.email               || prev.email,
      address:             patient.address             || prev.address,
      city:                patient.city                || prev.city,
      allergies:           patient.allergies           || prev.allergies,
      medical_history:     patient.medical_history     || prev.medical_history,
    }));
    setIsExistingPatient(true);
    setPatientOpen(false);
    if (errors.full_name) setErrors((prev) => ({ ...prev, full_name: "" }));
  };

  const handleStructureSelect = async (entry) => {
    if (formData.hospital_structure_id) {
      try {
        await hospitalStructureService.updateStatus(formData.hospital_structure_id, 0);
      } catch (err) {
        console.error("Failed to release previous structure:", err);
      }
    }
    if (!entry) {
      setFormData((prev) => ({ ...prev, hospital_structure_id: null, room_number: "", bed_number: "" }));
      return;
    }
    try {
      await hospitalStructureService.updateStatus(entry.id, 1);
    } catch (err) {
      toast.error("Failed to reserve the selected room/ward. Please try again.");
      return;
    }
    setFormData((prev) => ({
      ...prev,
      hospital_structure_id: entry.id,
      room_number: entry.entry_type === "room" ? entry.room_number || "" : "",
      bed_number:  entry.entry_type === "ward" ? entry.bed_number  || "" : "",
    }));
  };



  const validate = () => {
    const e = {};
    if (!formData.full_name.trim())            e.full_name      = "Required";
    if (!formData.gender)                      e.gender         = "Required";
    if (!formData.dOB)                         e.dOB            = "Required";
    if (!formData.address.trim())              e.address        = "Required";
    if (formData.CNIC.length !== 13)           e.CNIC           = "Must be 13 digits";
    if (formData.contact_number.length !== 11) e.contact_number = "Must be 11 digits";
    if (!formData.checkup_date)                e.checkup_date   = "Required";
    return e;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const validationErrors = validate();
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      toast.error("Please fix the highlighted errors.");
      return;
    }
    setLoading(true);
    try {
      const data = await admissionService.create(formData);
      if (data.responseCode === 200) {
        toast.success("Patient admitted successfully!");
        navigate("/admission");
      } else {
        toast.error("Failed to admit patient. Please try again.");
      }
    } catch (err) {
      toast.error("Server error. Please check your connection.");
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = async () => {
    if (formData.hospital_structure_id) {
      try {
        await hospitalStructureService.updateStatus(formData.hospital_structure_id, 0);
      } catch (err) {
        console.error("Failed to release structure on cancel:", err);
      }
    }
    navigate(-1);
  };



  const ErrMsg = ({ field }) =>
    errors[field] ? (
      <span className="text-[11px] text-red-400 mt-0.5">{errors[field]}</span>
    ) : null;


  return (
    <div className="p-3 sm:p-6">
      <div className="border border-gray-200 bg-white rounded-xl overflow-hidden shadow-sm">

        <div className="bg-[#215187] px-4 sm:px-6 py-4 sm:py-6">
          <h2 className="text-white text-[13px] sm:text-[14px] font-[500] uppercase tracking-widest">
            New Admission
          </h2>
          <p className="text-[12px] text-gray-400 mt-0.5">
            Fill in the details below to admit a new patient
          </p>
        </div>

        <form onSubmit={handleSubmit} className="px-4 sm:px-6 py-4 sm:py-6">

          <SectionLabel>Personal Information</SectionLabel>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">

      
            <div className="relative" ref={patientSearchRef}>
              <div className="flex flex-col gap-1">
                <label className="text-[12px] font-[500] text-gray-500">
                  Patient Name *
                  {isExistingPatient && (
                    <span className="ml-2 text-[10px] font-[600] text-green-500 bg-green-50 px-1.5 py-0.5 rounded-full">
                      ✓ Existing patient
                    </span>
                  )}
                </label>
                <input
                  name="full_name"
                  value={formData.full_name}
                  onChange={handlePatientNameChange}
                  onFocus={() => setPatientOpen(true)}
                  placeholder={
                    patientsLoading
                      ? "Loading patients..."
                      : "Search existing or type new name"
                  }
                  autoComplete="off"
                  className="w-full px-3 py-[9px] text-[13px] text-gray-800 bg-white border border-gray-200 rounded-lg outline-none hover:border-orange-200 focus:border-orange-300 focus:ring-2 focus:ring-orange-100 transition-colors"
                />
                <ErrMsg field="full_name" />
              </div>

      
              {patientOpen && formData.full_name.trim().length > 0 && (
                <div className="absolute z-30 w-full bg-white border border-gray-200 rounded-lg mt-1 shadow-lg overflow-hidden">

                  {filteredPatients.length > 0 && (
                    <>
                      <div className="px-3 py-1.5 text-[10px] uppercase tracking-widest font-[600] text-gray-400 bg-gray-50 border-b border-gray-100">
                        Existing Patients
                      </div>
                      <div className="max-h-48 overflow-y-auto">
                        {filteredPatients.slice(0, 8).map((patient) => (
                          <button
                            key={patient.patient_id}
                            type="button"
                            onMouseDown={(e) => e.preventDefault()}
                            onClick={() => handlePatientSelect(patient)}
                            className="w-full text-left px-3 py-2.5 hover:bg-orange-50 border-b border-gray-50 transition-colors"
                          >
                            <div className="flex items-center justify-between gap-2">
                              <div className="min-w-0">
                                <p className="text-[13px] text-gray-700 font-[500] truncate">
                                  {patient.full_name}
                                </p>
                                <p className="text-[11px] text-gray-400">
                                  {patient.contact_number}
                                </p>
                              </div>
                              <span className="text-[11px] text-blue-500 font-[500] bg-blue-50 px-2 py-0.5 rounded-full flex-shrink-0">
                                #{patient.patient_id}
                              </span>
                            </div>
                          </button>
                        ))}
                      </div>
                    </>
                  )}

       
                  {filteredPatients.length === 0 && formData.full_name.trim().length >= 2 && (
                    <button
                      type="button"
                      onMouseDown={(e) => e.preventDefault()}
                      onClick={() => setPatientOpen(false)}
                      className="w-full text-left px-3 py-3 hover:bg-gray-50 transition-colors"
                    >
                      <p className="text-[13px] text-gray-600">
                        No match — admitting as{" "}
                        <span className="font-[600] text-orange-500">
                          "{formData.full_name}"
                        </span>
                      </p>
                      <p className="text-[11px] text-gray-400 mt-0.5">
                        New patient — fill in the rest of the form manually
                      </p>
                    </button>
                  )}

                </div>
              )}
            </div>

            <InputField
              label="Father / Husband Name"
              name="father_husband_name"
              value={formData.father_husband_name}
              onChange={handleChange}
              placeholder="Enter father or husband name"
            />

            <div>
              <ValidatedField
                label="CNIC *"
                name="CNIC"
                value={formData.CNIC}
                maxLength={13}
                placeholder="13 digits, numbers only"
                hint="CNIC"
                onChange={(e) => {
                  setFormData((p) => ({ ...p, CNIC: e.target.value.replace(/\D/g, "").slice(0, 13) }));
                  if (errors.CNIC) setErrors((p) => ({ ...p, CNIC: "" }));
                }}
              />
              <ErrMsg field="CNIC" />
            </div>

            <div>
              <DOBInput value={formData.dOB} onChange={handleChange} />
              <ErrMsg field="dOB" />
            </div>

            <div>
              <CustomSelect
                label="Gender *"
                name="gender"
                value={formData.gender}
                onChange={handleChange}
                options={["Male", "Female", "Other"]}
              />
              <ErrMsg field="gender" />
            </div>

            <CustomSelect
              label="Marital Status"
              name="marital_status"
              value={formData.marital_status}
              onChange={handleChange}
              options={["Single", "Married", "Unmarried", "Widowed", "Other"]}
            />

            <InputField
              label="Nationality"
              name="nationality"
              value={formData.nationality}
              onChange={handleChange}
              placeholder="Enter nationality"
            />

            <InputField
              label="Occupation"
              name="occupation"
              value={formData.occupation}
              onChange={handleChange}
              placeholder="Enter occupation"
            />

            <CustomSelect
              label="Blood Group"
              name="blood_group"
              value={formData.blood_group}
              onChange={handleChange}
              options={["A+", "A-", "B+", "B-", "AB+", "AB-", "O+", "O-"]}
            />
          </div>

     
          <SectionLabel>Contact Information</SectionLabel>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            <div>
              <ValidatedField
                label="Mobile No *"
                name="contact_number"
                value={formData.contact_number}
                maxLength={11}
                placeholder="03XXXXXXXXX"
                hint="number"
                onChange={(e) => {
                  setFormData((p) => ({ ...p, contact_number: e.target.value.replace(/\D/g, "").slice(0, 11) }));
                  if (errors.contact_number) setErrors((p) => ({ ...p, contact_number: "" }));
                }}
              />
              <ErrMsg field="contact_number" />
            </div>

            <InputField
              label="Email"
              hint="(Optional)"
              name="email"
              value={formData.email}
              onChange={handleChange}
              type="email"
              placeholder="Enter email address"
            />

            <InputField
              label="City"
              name="city"
              value={formData.city}
              onChange={handleChange}
              placeholder="Enter city"
            />

            <div className="col-span-1 sm:col-span-2 lg:col-span-3">
              <InputField
                label="Full Address *"
                name="address"
                value={formData.address}
                onChange={handleChange}
                placeholder="House no, Street, Area, etc."
              />
              <ErrMsg field="address" />
            </div>
          </div>

         
          <SectionLabel>Medical Information</SectionLabel>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            <InputField
              label="Allergies"
              hint="(If any)"
              name="allergies"
              value={formData.allergies}
              onChange={handleChange}
              placeholder="Enter allergies"
            />
            <InputField
              label="Medical History"
              name="medical_history"
              value={formData.medical_history}
              onChange={handleChange}
              placeholder="Enter medical history"
            />
            <InputField
              label="Current Medications"
              name="current_medications"
              value={formData.current_medications}
              onChange={handleChange}
              placeholder="Enter current medications"
            />
            <InputField
              label="Previous Surgeries"
              name="previous_surgeries"
              value={formData.previous_surgeries}
              onChange={handleChange}
              placeholder="Enter previous surgeries"
            />
            <InputField
              label="Reason for Admission"
              name="admission_reason"
              value={formData.admission_reason}
              onChange={handleChange}
              placeholder="Enter symptoms or reason"
            />
            <CustomSelect
              label={doctorsLoading ? "Doctor Assigned (Loading...)" : "Doctor Assigned"}
              name="doctor_assigned"
              value={formData.doctor_assigned}
              onChange={handleChange}
              options={doctors.map((d) => ({
                value: d.name,
                label: d.name + (d.specialization ? " — " + d.specialization : ""),
              }))}
            />
          </div>

      
          <SectionLabel>Vitals</SectionLabel>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
            <InputField label="Weight" hint="(kg)" name="weight_kg" value={formData.weight_kg} onChange={handleChange} placeholder="e.g. 70" type="number" />
            <InputField label="Temperature" hint="(°C)" name="temperature_c" value={formData.temperature_c} onChange={handleChange} placeholder="e.g. 37.0" type="number" />
            <InputField label="Blood Pressure" name="blood_pressure" value={formData.blood_pressure} onChange={handleChange} placeholder="120/80" />
            <InputField label="Pulse Rate" hint="(bpm)" name="pulse_rate" value={formData.pulse_rate} onChange={handleChange} placeholder="e.g. 72" />
            <InputField label="Blood Sugar" name="blood_sugar" value={formData.blood_sugar} onChange={handleChange} placeholder="e.g. 110" />
          </div>

     
          <SectionLabel>Admission Information</SectionLabel>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            <CustomSelect
              label="Admission Type"
              name="admission_type"
              value={formData.admission_type}
              onChange={handleChange}
              options={["Emergency", "Elective", "Maternity", "Day Care", "ICU", "Other"]}
            />
            <InputField
              label="Admission Date & Time"
              name="admission_datetime"
              value={formData.admission_datetime}
              onChange={handleChange}
              type="datetime-local"
            />
            <div>
              <InputField
                label="Checkup Date *"
                name="checkup_date"
                value={formData.checkup_date}
                onChange={handleChange}
                type="date"
              />
              <ErrMsg field="checkup_date" />
            </div>

            <div className="col-span-1 sm:col-span-2 lg:col-span-3">
              <HospitalStructureSelect
                value={formData.hospital_structure_id}
                onChange={handleStructureSelect}
                structureList={structureList}
                loading={structureLoading}
                error={structureError}
              />
            </div>

            {(formData.room_number || formData.bed_number) && (
              <>
                {formData.room_number && (
                  <div className="flex flex-col gap-1">
                    <label className="text-[12px] font-[500] text-gray-500">
                      Room No <span className="text-gray-400 font-[400]">(auto-filled)</span>
                    </label>
                    <input
                      readOnly
                      value={formData.room_number}
                      className="w-full px-3 py-[9px] text-[13px] text-gray-600 bg-gray-50 border border-gray-200 rounded-lg cursor-not-allowed"
                    />
                  </div>
                )}
                {formData.bed_number && (
                  <div className="flex flex-col gap-1">
                    <label className="text-[12px] font-[500] text-gray-500">
                      Bed No <span className="text-gray-400 font-[400]">(auto-filled)</span>
                    </label>
                    <input
                      readOnly
                      value={formData.bed_number}
                      className="w-full px-3 py-[9px] text-[13px] text-gray-600 bg-gray-50 border border-gray-200 rounded-lg cursor-not-allowed"
                    />
                  </div>
                )}
              </>
            )}

            <InputField
              label="Referred By"
              hint="(Optional)"
              name="referred_by"
              value={formData.referred_by}
              onChange={handleChange}
              placeholder="Enter referred by"
            />
          </div>

  
          <SectionLabel>Guardian Information</SectionLabel>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            <InputField
              label="Guardian Name"
              name="guardian_name"
              value={formData.guardian_name}
              onChange={handleChange}
              placeholder="Enter guardian name"
            />
            <ValidatedField
              label="Guardian CNIC"
              name="guardian_CNIC"
              value={formData.guardian_CNIC}
              maxLength={13}
              placeholder="13 digits, numbers only"
              hint="CNIC"
              onChange={(e) =>
                setFormData((p) => ({ ...p, guardian_CNIC: e.target.value.replace(/\D/g, "").slice(0, 13) }))
              }
            />
            <ValidatedField
              label="Emergency Contact"
              name="emergency_contact"
              value={formData.emergency_contact}
              maxLength={11}
              placeholder="03XXXXXXXXX"
              hint="number"
              onChange={(e) =>
                setFormData((p) => ({ ...p, emergency_contact: e.target.value.replace(/\D/g, "").slice(0, 11) }))
              }
            />
            <InputField
              label="Relationship With Patient"
              name="guardian_relationship"
              value={formData.guardian_relationship}
              onChange={handleChange}
              placeholder="e.g. Brother, Father"
            />
          </div>

         
          <div className="border-t border-gray-200 mt-8 pt-5 flex flex-col-reverse sm:flex-row items-stretch sm:items-center justify-between gap-3">
            <button
              type="button"
              onClick={handleCancel}
              className="text-[13px] text-gray-400 hover:text-gray-600 transition-colors font-[500] text-center sm:text-left py-2 sm:py-0"
            >
              ← Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className="flex items-center justify-center gap-2 bg-[#2F6DB4] hover:bg-[#255A8C] disabled:bg-gray-400 transition-colors text-white text-[13px] font-[500] px-6 py-[10px] sm:py-[7px] rounded-lg w-full sm:w-auto"
            >
              {loading ? "Saving..." : "Save & Admit →"}
            </button>
          </div>

        </form>
      </div>

      <p className="text-center text-[12px] text-gray-400 mt-4">
        Fields marked with * are required.
      </p>
    </div>
  );
};

export default AddAdmitPatient;